import api from './api';

const adminService = {
  // User Management
  getUserDetails: async (userId) => {
    const response = await api.get(`/admin/user-details/${userId}`);
    return response.data;
  },

  getAllUsers: async (params) => {
    const safeParams = { ...(params || {}) };
    // Backend now expects this endpoint without role filter
    delete safeParams.role;
    const response = await api.get('/admin/all-users', {
      params: safeParams,
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    });
    return response.data;
  },

  downloadUsers: async (params) => {
    const safeParams = { ...(params || {}) };
    delete safeParams.role;
    const response = await api.get('/admin/all-users/download', {
      params: safeParams,
      responseType: 'blob',
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    });
    return response;
  },

  updateUserStatus: async (userId, data) => {
    const response = await api.patch(`/admin/status/${userId}`, data);
    return response.data;
  },

  deleteUser: async (userId) => {
    const response = await api.delete(`/admin/delete/${userId}`);
    return response.data;
  },

  verifyUserKyc: async (userId, data) => {
    const response = await api.patch(`/admin/verify-kyc/${userId}`, data);
    return response.data;
  },

  startReviewKyc: async (userId) => {
    const response = await api.patch(`/admin/start-review-kyc/${userId}`);
    return response.data;
  },

  registerStaff: async (data) => {
    const response = await api.post('/admin/register-staff', data);
    return response.data;
  },

  // Donation Analytics
  getFinancialStats: async () => {
    const response = await api.get('/admin/donation/stats');
    return response.data;
  },

  getDonationAnalytics: async () => {
    const response = await api.get('/admin/donation/analytics');
    return response.data;
  },

  getDonationsList: async (params, isAdmin = false) => {
    const url = isAdmin ? '/admin/donation/admin/list' : '/admin/donation/list';
    const response = await api.get(url, { params });
    return response.data;
  },

  exportDonations: async (params) => {
    const response = await api.get('/admin/donation/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  approveVoucher: async (id) => {
    const response = await api.post(`/admin/donation/approve-voucher/${id}`);
    return response.data;
  },

  rejectVoucher: async (id, data = {}) => {
    const response = await api.post(`/admin/donation/reject-voucher/${id}`, data);
    return response.data;
  },

  // Banner Management
  getBannerSettings: async () => {
    const response = await api.get('/settings/banner');
    return response.data;
  },

  updateBannerImage: async (formData) => {
    const response = await api.post('/admin/banner/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Bank Settings Management
  getBankSettings: async () => {
    try {
      const response = await api.get('/settings/bank');
      // Axios returns .data, then API wraps in .data.data (usually)
      // The user provided structure: root.data (Axios) -> root.data.data (API) -> root.data.data.data (Payload)
      let data = response.data;
      if (data && data.data && data.data.data) {
        return { data: data.data.data };
      }
      return data;
    } catch (error) {
      // Fallback to localStorage if API fails (useful for dev/testing before backend is ready)
      const localData = localStorage.getItem('local_bank_settings');
      if (localData) {
        return { data: JSON.parse(localData) };
      }
      throw error;
    }
  },

  updateBankSettings: async (data) => {
    try {
      const response = await api.post('/admin/settings/bank', data);
      return response.data;
    } catch (error) {
      // Save to localStorage if API fails
      localStorage.setItem('local_bank_settings', JSON.stringify(data));
      return { status: 'success', message: 'Saved to local storage (Backend API not found)' };
    }
  },

  updateQrCode: async (formData) => {
    try {
      const response = await api.post('/admin/settings/bank/qr-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      // Handle potential triple nesting in upload response
      let data = response.data;
      if (data && data.data && data.data.data) {
        return { data: data.data.data };
      }
      return data;
    } catch (error) {
      // Handle QR code fallback to localStorage as base64
      const file = formData.get('file');
      if (file) {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64 = reader.result;
            const localData = JSON.parse(localStorage.getItem('local_bank_settings') || '{}');
            localData.qrCodeUrl = base64;
            localStorage.setItem('local_bank_settings', JSON.stringify(localData));
            resolve({ status: 'success', qrCodeUrl: base64, message: 'QR saved locally' });
          };
          reader.readAsDataURL(file);
        });
      }
      throw error;
    }
  },
};

export default adminService;
